import React, { useState, useMemo, useCallback } from 'react';
import {
  Eye, Settings, Target, Activity, User, Clock, Gauge, Zap, Thermometer,
  ThumbsUp, ThumbsDown, ChevronUp, ChevronDown,
} from 'lucide-react';
import { Button } from './ui/button';
import { WeldSegmentViewFilters } from './ui/WeldSegmentViewFilters';
import { formatDurationFromSeconds, formatTimestamp } from '../lib/formatUtils';
import { convertWeldDataValue, getParameterUnitSuffix } from '../utils/unitUtils';
import { HEAT_INPUT_DISPLAY_NAME } from '../utils/weldParameterLabels';
import { isManualWeldingHiddenColumn, formatManualWeldingBugTypeLabel } from '../utils/manualWeldingUtils';

export interface WeldSegmentRecord {
  id?: number;
  s_event?: string | null;
  t_event?: string | null;
  welduid_s_event_torch?: string | null;
  welduid_t_event_torch?: string | null;
  s_time?: number | null;
  arc_time?: number | null;
  pass_name?: string | null;
  torch?: string | null;
  station_number?: number | null;
  bug_type?: string | null;
  job_number?: string | null;
  segment_avg_pass_status?: string | null;
  current_avg?: number | null;
  current_high?: number | null;
  current_low?: number | null;
  current_avg_status?: boolean | null;
  volts_avg?: number | null;
  volts_high?: number | null;
  volts_low?: number | null;
  volts_avg_status?: boolean | null;
  travel_speed_avg?: number | null;
  travel_speed_high?: number | null;
  travel_speed_low?: number | null;
  travel_speed_avg_status?: boolean | null;
  wire_speed_avg?: number | null;
  wire_speed_high?: number | null;
  wire_speed_low?: number | null;
  wire_speed_avg_status?: boolean | null;
  true_energy_avg?: number | null;
  true_energy_high?: number | null;
  true_energy_low?: number | null;
  true_energy_avg_status?: boolean | null;
  heat_avg?: number | null;
  heat_high?: number | null;
  heat_low?: number | null;
  heat_avg_status?: boolean | null;
  oscillation_width_avg?: number | null;
  oscillation_width_high?: number | null;
  oscillation_width_low?: number | null;
  oscillation_width_avg_status?: boolean | null;
  arc_impedance_avg?: number | null;
  arc_impedance_high?: number | null;
  arc_impedance_low?: number | null;
  arc_impedance_avg_status?: boolean | null;
}

interface WeldSegmentViewTableProps {
  segments: WeldSegmentRecord[];
  loading?: boolean;
  onSegmentClick?: (segment: WeldSegmentRecord) => void;
  visibleColumns?: string[];
}

interface Sorting {
  field: string;
  order: 'ascend' | 'descend';
}

type ParamValues = {
  min: number | null;
  avg: number | null;
  max: number | null;
  avgStatus?: boolean | null;
};

const PARAMETER_LIMITS = {
  voltage: { min: 20, max: 30 },
  current: { min: 150, max: 250 },
  travelSpeed: { min: 100, max: 300 },
  heat: { min: 1.0, max: 2.0 },
  trueEnergy: { min: 2.5, max: 4.5 },
  oscillationWidth: { min: 0.01, max: 10.0 },
  wireSpeed: { min: 150, max: 450 },
};

const scrollbarStyles = `
  .scrollbar-thin::-webkit-scrollbar { height: 8px; width: 8px; }
  .scrollbar-thin::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
  .scrollbar-thin::-webkit-scrollbar-thumb { background: #9ca3af; border-radius: 4px; }
  .scrollbar-thin { scrollbar-width: thin; scrollbar-color: #9ca3af #f1f1f1; scroll-behavior: smooth; }
  @media (max-width: 768px) {
    .mobile-scroll-container { -webkit-overflow-scrolling: touch; overscroll-behavior: contain; }
    .parameter-cell { min-width: 140px; }
  }
`;

const getSegmentParam = (segment: WeldSegmentRecord, prefix: string): ParamValues => ({
  min: (segment[`${prefix}_low` as keyof WeldSegmentRecord] as number | null | undefined) ?? null,
  avg: (segment[`${prefix}_avg` as keyof WeldSegmentRecord] as number | null | undefined) ?? null,
  max: (segment[`${prefix}_high` as keyof WeldSegmentRecord] as number | null | undefined) ?? null,
  avgStatus: segment[`${prefix}_avg_status` as keyof WeldSegmentRecord] as boolean | null | undefined,
});

const renderStatusIcon = (status?: string | null) => {
  if (status === 'TRUE') {
    return <ThumbsUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />;
  }
  if (status === 'FALSE') {
    return <ThumbsDown className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />;
  }
  if (status === 'EX') {
    return <span className="h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center font-bold text-xs sm:text-sm bg-yellow-400 text-black rounded-full">!</span>;
  }
  if (status === 'CROSS') {
    return <span className="h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center font-bold text-xs sm:text-sm text-red-600">×</span>;
  }
  if (status === 'NULL' || status == null || status === undefined) {
    return <span className="text-gray-600">-</span>;
  }
  return <div className="h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-yellow-400 animate-pulse" />;
};

const WeldSegmentViewTable: React.FC<WeldSegmentViewTableProps> = ({
  segments,
  loading = false,
  onSegmentClick,
  visibleColumns,
}) => {
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [passNameFilter, setPassNameFilter] = useState<string[]>([]);
  const [bugTypeFilter, setBugTypeFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ dateFrom: Date | undefined; dateTo: Date | undefined }>({
    dateFrom: undefined,
    dateTo: undefined,
  });
  const [sorting, setSorting] = useState<Sorting | null>(null);

  const isColumnVisible = useCallback((columnKey: string): boolean => {
    if (isManualWeldingHiddenColumn(columnKey)) return false;
    if (!visibleColumns || visibleColumns.length === 0) return true;
    if (visibleColumns.includes(columnKey)) return true;
    return ['min', 'avg', 'max'].some((sub) => visibleColumns.includes(`${columnKey}-${sub}`));
  }, [visibleColumns]);

  const isSubColumnVisible = useCallback((columnKey: string, subType: 'min' | 'avg' | 'max'): boolean => {
    if (!visibleColumns || visibleColumns.length === 0) return true;
    return visibleColumns.includes(`${columnKey}-${subType}`);
  }, [visibleColumns]);

  const getStickyLeft = useCallback((columnKey: string): string => {
    // Status sticky column temporarily disabled — re-enable with Status header/cell
    const stickyColumnOrder = ['sl-no', /* 'status', */ 'station', 'actions'];
    const stickyColumnWidths: Record<string, number> = {
      'sl-no': 80,
      // status: 60,
      station: 120,
      actions: 100,
    };

    let left = 0;
    const currentIndex = stickyColumnOrder.indexOf(columnKey);
    for (let i = 0; i < currentIndex; i++) {
      const key = stickyColumnOrder[i];
      if (isColumnVisible(key)) {
        left += stickyColumnWidths[key];
      }
    }
    return `${left}px`;
  }, [isColumnVisible]);

  const getUnitLabel = (parameter: string): string => getParameterUnitSuffix(parameter);

  const renderHeaderLabels = (columnKey: string) => {
    const visibleSubs: ('min' | 'avg' | 'max')[] = [];
    if (isSubColumnVisible(columnKey, 'min')) visibleSubs.push('min');
    if (isSubColumnVisible(columnKey, 'avg')) visibleSubs.push('avg');
    if (isSubColumnVisible(columnKey, 'max')) visibleSubs.push('max');
    if (visibleSubs.length === 0) return null;

    const labels: Record<'min' | 'avg' | 'max', string> = { min: 'Min', avg: 'Avg', max: 'Max' };
    return (
      <div
        className="grid text-xs font-normal text-center text-gray-500"
        style={{ gridTemplateColumns: `repeat(${visibleSubs.length}, minmax(0, 1fr))` }}
      >
        {visibleSubs.map((sub) => (
          <div key={sub}>{labels[sub]}</div>
        ))}
      </div>
    );
  };

  const renderMinAvgMax = (
    values?: ParamValues,
    decimals: number = 2,
    paramName?: keyof typeof PARAMETER_LIMITS,
    columnKey?: string
  ) => {
    if (!values || (values.min === null && values.avg === null && values.max === null)) {
      const visibleSubs: ('min' | 'avg' | 'max')[] = [];
      if (!columnKey || isSubColumnVisible(columnKey, 'min')) visibleSubs.push('min');
      if (!columnKey || isSubColumnVisible(columnKey, 'avg')) visibleSubs.push('avg');
      if (!columnKey || isSubColumnVisible(columnKey, 'max')) visibleSubs.push('max');
      if (visibleSubs.length === 0) return null;

      return (
        <div className="grid gap-1 h-full" style={{ gridTemplateColumns: `repeat(${visibleSubs.length}, minmax(0, 1fr))` }}>
          {visibleSubs.map((_, idx) => (
            <div key={idx} className="text-gray-400 flex items-center justify-center h-full text-xs">-</div>
          ))}
        </div>
      );
    }

    const renderValue = (value: number | null, isAvg = false, status?: boolean | null) => {
      if (value === null) return '-';

      let displayValue: string;
      if (paramName && ['travelSpeed', 'wireSpeed', 'heat', 'trueEnergy', 'oscillationWidth'].includes(paramName)) {
        const convertedValue = convertWeldDataValue(value, paramName);
        displayValue = typeof convertedValue === 'number' ? convertedValue.toFixed(decimals) : '-';
      } else {
        displayValue = value.toFixed(decimals);
      }

      const bgColor = status === false ? 'bg-red-100' : 'bg-white';
      const textColor = status === false ? 'text-red-800' : isAvg ? 'text-gray-900' : 'text-blue-600';

      return (
        <div className={`flex items-center justify-center h-full w-full ${bgColor} ${textColor} rounded-lg px-1 font-medium text-xs break-all`}>
          {displayValue}
        </div>
      );
    };

    const visibleSubs: Array<{ type: 'min' | 'avg' | 'max'; value: number | null; status?: boolean | null }> = [];
    if (!columnKey || isSubColumnVisible(columnKey, 'min')) {
      visibleSubs.push({ type: 'min', value: values.min });
    }
    if (!columnKey || isSubColumnVisible(columnKey, 'avg')) {
      visibleSubs.push({ type: 'avg', value: values.avg, status: values.avgStatus });
    }
    if (!columnKey || isSubColumnVisible(columnKey, 'max')) {
      visibleSubs.push({ type: 'max', value: values.max });
    }
    if (visibleSubs.length === 0) return null;

    return (
      <div
        className="grid gap-1 rounded-md h-full bg-gray-50 min-w-0"
        style={{ gridTemplateColumns: `repeat(${visibleSubs.length}, minmax(0, 1fr))` }}
      >
        {visibleSubs.map((sub) => (
          <div key={sub.type} className="flex items-center justify-center h-full min-w-0">
            {renderValue(sub.value, sub.type === 'avg', sub.status)}
          </div>
        ))}
      </div>
    );
  };

  const handleSort = (field: string) => {
    if (sorting?.field === field) {
      setSorting(sorting.order === 'ascend' ? { field, order: 'descend' } : null);
    } else {
      setSorting({ field, order: 'ascend' });
    }
  };

  const renderSortIcon = (field: string) => {
    if (sorting?.field !== field) return <ChevronUp className="h-3 w-3 text-gray-400" />;
    return sorting.order === 'ascend'
      ? <ChevronUp className="h-3 w-3 text-blue-600" />
      : <ChevronDown className="h-3 w-3 text-blue-600" />;
  };

  const filterOptions = useMemo(() => ({
    passNames: Array.from(new Set(segments.map((s) => s.pass_name).filter(Boolean))) as string[],
    bugTypes: Array.from(new Set(segments.map((s) => s.bug_type).filter(Boolean))) as string[],
  }), [segments]);

  const filteredSegments = useMemo(() => {
    let result = segments.filter((segment) => {
      if (statusFilter.length > 0) {
        const status = segment.segment_avg_pass_status || 'NULL';
        if (!statusFilter.includes(status)) return false;
      }
      if (passNameFilter.length > 0 && (!segment.pass_name || !passNameFilter.includes(segment.pass_name))) return false;
      if (bugTypeFilter.length > 0 && (!segment.bug_type || !bugTypeFilter.includes(segment.bug_type))) return false;
      if (dateRange.dateFrom || dateRange.dateTo) {
        if (segment.s_time == null) return false;
        const segmentDate = new Date(Number(segment.s_time) * 1000);
        if (dateRange.dateFrom && segmentDate < dateRange.dateFrom) return false;
        if (dateRange.dateTo) {
          const endOfDay = new Date(dateRange.dateTo);
          endOfDay.setHours(23, 59, 59, 999);
          if (segmentDate > endOfDay) return false;
        }
      }
      return true;
    });

    if (sorting) {
      result = [...result].sort((a, b) => {
        const dir = sorting.order === 'ascend' ? 1 : -1;
        const getVal = (row: WeldSegmentRecord) => {
          switch (sorting.field) {
            case 'status': return row.segment_avg_pass_status || '';
            case 'event': return row.t_event || '';
            case 'bugType': return row.bug_type || '';
            case 'time': return row.s_time ?? 0;
            case 'arcTime': return row.arc_time ?? 0;
            case 'pass': return row.pass_name || '';
            case 'torch': return row.torch || '';
            case 'stationNumber': return row.station_number ?? 0;
            default: return '';
          }
        };
        const av = getVal(a);
        const bv = getVal(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }

    return result;
  }, [segments, statusFilter, passNameFilter, bugTypeFilter, dateRange, sorting]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]" />
          <span className="text-gray-600 text-lg">Loading weld segments...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col w-full overflow-hidden">
      <style>{scrollbarStyles}</style>

      <div className="flex-shrink-0 px-2 sm:px-4">
        <WeldSegmentViewFilters
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          passNameFilter={passNameFilter}
          onPassNameFilterChange={setPassNameFilter}
          bugTypeFilter={bugTypeFilter}
          onBugTypeFilterChange={setBugTypeFilter}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          passNameOptions={filterOptions.passNames}
          bugTypeOptions={filterOptions.bugTypes}
          totalCount={segments.length}
          filteredCount={filteredSegments.length}
        />
      </div>

      <div className="flex-1 relative overflow-hidden">
        {filteredSegments.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500 text-center">
              <p>No weld segment data found for this pass.</p>
              {(statusFilter.length > 0 || passNameFilter.length > 0 || bugTypeFilter.length > 0 || dateRange.dateFrom || dateRange.dateTo) && (
                <p className="text-sm mt-2">Try adjusting your filters.</p>
              )}
            </div>
          </div>
        ) : (
          <div
            className="absolute inset-0 overflow-x-auto overflow-y-auto pb-20 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 mobile-scroll-container"
            style={{ WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain' }}
          >
            <table className="border-collapse w-full" style={{ tableLayout: 'auto', minWidth: 'max-content' }}>
              <thead className="sticky top-0 bg-white z-30">
                <tr className="border-b h-[72px] bg-gray-50">
                  {isColumnVisible('sl-no') && (
                    <th className="sticky left-0 z-20 bg-gray-50 min-w-[60px] w-[80px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                      <div className="flex items-center justify-center gap-1">
                        <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">Sl.no</span>
                        <span className="sm:hidden">#</span>
                      </div>
                    </th>
                  )}
                  {/* Status column temporarily disabled for weld segment view — re-enable when needed
                  {isColumnVisible('status') && (
                    <th className="sticky z-20 bg-gray-50 min-w-[60px] w-[60px] max-w-[60px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ width: '60px', left: getStickyLeft('status') }}>
                      <button onClick={() => handleSort('status')} className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full">
                        <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">Status</span>
                        {renderSortIcon('status')}
                      </button>
                    </th>
                  )}
                  */}
                  {isColumnVisible('station') && (
                    <th className="sticky z-20 bg-gray-50 min-w-[100px] w-[120px] sm:w-[140px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('station') }}>
                      <button onClick={() => handleSort('stationNumber')} className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full">
                        <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                        Station
                        {renderSortIcon('stationNumber')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('actions') && (
                    <th className="sticky z-20 bg-gray-50 min-w-[80px] w-[80px] sm:w-[100px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600" style={{ left: getStickyLeft('actions'), boxShadow: '4px 0 6px -2px rgba(0,0,0,0.2)' }}>
                      <div className="flex items-center justify-center gap-1">
                        <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                        Actions
                      </div>
                    </th>
                  )}

                  {isColumnVisible('event') && (
                    <th className="min-w-[120px] w-[140px] sm:w-[160px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600">
                      <button onClick={() => handleSort('event')} className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full">
                        <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
                        Event
                        {renderSortIcon('event')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('bug-type') && (
                    <th className="min-w-[100px] w-[120px] sm:w-[140px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600">
                      <button onClick={() => handleSort('bugType')} className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full">
                        <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                        Bug Type
                        {renderSortIcon('bugType')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('time') && (
                    <th className="min-w-[140px] w-[140px] sm:w-[160px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600">
                      <button onClick={() => handleSort('time')} className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full">
                        <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                        Time
                        {renderSortIcon('time')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('arc-time') && (
                    <th className="min-w-[90px] w-[90px] sm:w-[100px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600">
                      <button onClick={() => handleSort('arcTime')} className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full">
                        <Gauge className="h-3 w-3 sm:h-4 sm:w-4" />
                        Arc Time
                        {renderSortIcon('arcTime')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('pass') && (
                    <th className="min-w-[120px] w-[140px] sm:w-[160px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600">
                      <button onClick={() => handleSort('pass')} className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full">
                        <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                        Pass
                        {renderSortIcon('pass')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('torch') && (
                    <th className="min-w-[80px] w-[100px] sm:w-[140px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600 border-r">
                      <button onClick={() => handleSort('torch')} className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full">
                        <User className="h-3 w-3 sm:h-4 sm:w-4" />
                        Torch
                        {renderSortIcon('torch')}
                      </button>
                    </th>
                  )}

                  {isColumnVisible('current') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1"><Zap className="h-4 w-4" />Current<br />(A)</div>
                      </div>
                      {renderHeaderLabels('current')}
                    </th>
                  )}
                  {isColumnVisible('voltage') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1"><Zap className="h-4 w-4" />Voltage<br />(V)</div>
                      </div>
                      {renderHeaderLabels('voltage')}
                    </th>
                  )}
                  {isColumnVisible('travel-speed') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1"><Gauge className="h-4 w-4" />Travel Speed<br />({getUnitLabel('travel_speed')})</div>
                      </div>
                      {renderHeaderLabels('travel-speed')}
                    </th>
                  )}
                  {isColumnVisible('true-energy') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1"><Activity className="h-4 w-4" />True Energy<br />({getUnitLabel('true_energy')})</div>
                      </div>
                      {renderHeaderLabels('true-energy')}
                    </th>
                  )}
                  {isColumnVisible('heat') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1"><Thermometer className="h-4 w-4" />{HEAT_INPUT_DISPLAY_NAME}<br />({getUnitLabel('heat')})</div>
                      </div>
                      {renderHeaderLabels('heat')}
                    </th>
                  )}
                  {isColumnVisible('wire-speed') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1"><Gauge className="h-4 w-4" />Wire Speed<br />({getUnitLabel('wire_speed')})</div>
                      </div>
                      {renderHeaderLabels('wire-speed')}
                    </th>
                  )}
                  {isColumnVisible('oscillation-width') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1"><Gauge className="h-4 w-4" />Oscillation Width<br />({getUnitLabel('oscillation_width')})</div>
                      </div>
                      {renderHeaderLabels('oscillation-width')}
                    </th>
                  )}
                  {isColumnVisible('arc-impedance') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1"><Zap className="h-4 w-4" />Arc Impedance<br />(mOhm)</div>
                      </div>
                      {renderHeaderLabels('arc-impedance')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredSegments.map((segment, index) => {
                  const rowBg = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                  return (
                    <tr key={segment.id ?? `${segment.s_event}-${segment.t_event}-${index}`} className={`h-[52px] ${rowBg} ${index < filteredSegments.length - 1 ? 'border-b' : ''}`}>
                      {isColumnVisible('sl-no') && (
                        <td className={`sticky z-10 ${rowBg} px-2 sm:px-4 text-center text-gray-600 text-xs font-medium shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ left: getStickyLeft('sl-no') }}>
                          {index + 1}
                        </td>
                      )}
                      {/* Status column temporarily disabled for weld segment view — re-enable when needed
                      {isColumnVisible('status') && (
                        <td className={`sticky z-10 ${rowBg} min-w-[60px] w-[60px] max-w-[60px] px-2 sm:px-4 text-center shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ width: '60px', left: getStickyLeft('status') }}>
                          <div className="inline-flex justify-center">
                            {renderStatusIcon(segment.segment_avg_pass_status)}
                          </div>
                        </td>
                      )}
                      */}
                      {isColumnVisible('station') && (
                        <td className={`sticky z-10 ${rowBg} px-2 sm:px-4 text-center text-gray-600 text-xs shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ left: getStickyLeft('station') }}>
                          {segment.station_number != null ? `Station ${segment.station_number}` : '-'}
                        </td>
                      )}
                      {isColumnVisible('actions') && (
                        <td className={`sticky z-10 ${rowBg} px-2 sm:px-4 text-center`} style={{ left: getStickyLeft('actions'), boxShadow: '4px 0 6px -2px rgba(0,0,0,0.2)' }}>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="hover:bg-gray-100 p-1 sm:p-2"
                              onClick={() => onSegmentClick?.(segment)}
                              title="View segment detail"
                            >
                              <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </div>
                        </td>
                      )}

                      {isColumnVisible('event') && (
                        <td className="px-2 sm:px-4 text-center text-gray-600 text-xs">{segment.t_event || '-'}</td>
                      )}
                      {isColumnVisible('bug-type') && (
                        <td className="px-2 sm:px-4 text-center text-gray-600 text-xs">{formatManualWeldingBugTypeLabel(segment.bug_type)}</td>
                      )}
                      {isColumnVisible('time') && (
                        <td className="px-2 sm:px-4 text-center text-gray-600 text-xs">
                          {segment.s_time != null ? formatTimestamp(segment.s_time) : '-'}
                        </td>
                      )}
                      {isColumnVisible('arc-time') && (
                        <td className="px-2 sm:px-4 text-center text-gray-600 text-xs">
                          {segment.arc_time != null ? formatDurationFromSeconds(segment.arc_time) : '-'}
                        </td>
                      )}
                      {isColumnVisible('pass') && (
                        <td className="px-2 sm:px-4 text-center font-medium text-xs">{segment.pass_name || '-'}</td>
                      )}
                      {isColumnVisible('torch') && (
                        <td className="px-2 sm:px-4 text-center text-gray-600 text-xs border-r">{segment.torch || '-'}</td>
                      )}

                      {isColumnVisible('current') && (
                        <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">
                          {renderMinAvgMax(getSegmentParam(segment, 'current'), 0, 'current', 'current')}
                        </td>
                      )}
                      {isColumnVisible('voltage') && (
                        <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">
                          {renderMinAvgMax(getSegmentParam(segment, 'volts'), 2, 'voltage', 'voltage')}
                        </td>
                      )}
                      {isColumnVisible('travel-speed') && (
                        <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">
                          {renderMinAvgMax(getSegmentParam(segment, 'travel_speed'), 1, 'travelSpeed', 'travel-speed')}
                        </td>
                      )}
                      {isColumnVisible('true-energy') && (
                        <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">
                          {renderMinAvgMax(getSegmentParam(segment, 'true_energy'), 2, 'trueEnergy', 'true-energy')}
                        </td>
                      )}
                      {isColumnVisible('heat') && (
                        <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">
                          {renderMinAvgMax(getSegmentParam(segment, 'heat'), 2, 'heat', 'heat')}
                        </td>
                      )}
                      {isColumnVisible('wire-speed') && (
                        <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">
                          {renderMinAvgMax(getSegmentParam(segment, 'wire_speed'), 1, 'wireSpeed', 'wire-speed')}
                        </td>
                      )}
                      {isColumnVisible('oscillation-width') && (
                        <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">
                          {renderMinAvgMax(getSegmentParam(segment, 'oscillation_width'), 2, 'oscillationWidth', 'oscillation-width')}
                        </td>
                      )}
                      {isColumnVisible('arc-impedance') && (
                        <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">
                          {renderMinAvgMax(getSegmentParam(segment, 'arc_impedance'), 2, undefined, 'arc-impedance')}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeldSegmentViewTable;
